import type { ProductionPanel } from "@/types/productionBrief.type";
import { cn } from "@/utils/helpers";
import { parseRatio, pictureBox } from "./briefParts";
import { BriefPicture, ChooseVisualTile, FieldLabel, resolveBriefPicture } from "./BriefPrimitives";
import { BriefRowFrame, Lane, RowNotes } from "./BriefShotRow";

/** Slides default to 4:5 when the panel does not say. */
const DEFAULT_SLIDE_RATIO = "4:5";

function slideRatio(panel: ProductionPanel): string {
  return parseRatio(panel.aspectRatio) ? panel.aspectRatio!.trim() : DEFAULT_SLIDE_RATIO;
}

/**
 * A slide's picture. An editor gets the picture picker (`onChoose`): Choose a
 * picture when empty (or when the picture will not load), Change when filled.
 * A read-only reader gets an empty tile: the copy on the slide reads in its
 * lane, never drawn over the frame.
 */
function SlidePicture({
  panel,
  onChoose,
}: {
  panel: ProductionPanel;
  onChoose?: () => void;
}) {
  const ratio = slideRatio(panel);
  const box = pictureBox(ratio);
  const picture = resolveBriefPicture(panel.resolvedVisual, panel.referenceUrl);
  const name = panel.role?.trim() || "this slide";
  // Only drawn when there is no picture to show, so it is set either way.
  const fallback = onChoose ? (
    <ChooseVisualTile label={`Choose a picture for ${name}`} onChoose={onChoose} />
  ) : (
    <div className="size-full bg-[var(--co-tonal)]" aria-hidden="true" />
  );
  return (
    <BriefPicture
      picture={picture}
      width={box.width}
      height={box.height}
      alt={panel.role?.trim() || "Slide"}
      fallback={fallback}
      onChange={onChoose}
      changeLabel={`Change the picture for ${name}`}
      fluid
    />
  );
}

/** Every slide at its ratio in swipe order; scrolls sideways past four. */
export function SlideStrip({
  panels,
  onChooseVisual,
}: {
  panels: ProductionPanel[];
  /** Present when the reader can save a pick: empty slides open the visual picker. */
  onChooseVisual?: (index: number) => void;
}) {
  if (!panels.length) return null;
  const many = panels.length > 4;
  return (
    <div className={cn("mb-2 overflow-x-auto pb-1 @[36rem]/briefdoc:pb-0", many ? "@[36rem]/briefdoc:pb-2" : "@[36rem]/briefdoc:overflow-visible")}>
      <div className="flex gap-4">
        {panels.map((panel, index) => (
          <figure
            key={panel.id ?? index}
            className={cn("w-[150px] min-w-0 max-w-[302px] shrink-0", many ? "@[36rem]/briefdoc:basis-[calc((100%-48px)/4)]" : "@[36rem]/briefdoc:w-auto @[36rem]/briefdoc:flex-1 @[36rem]/briefdoc:shrink @[36rem]/briefdoc:basis-0")}
          >
            <SlidePicture panel={panel} onChoose={onChooseVisual ? () => onChooseVisual(index) : undefined} />
            <figcaption className="mt-2 flex gap-2.5 text-[12.5px] leading-snug">
              <span className="text-co-muted">{index + 1}</span>
              {panel.role?.trim() ? <span className="text-co-text2">{panel.role.trim()}</span> : null}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}

export function SlideRow({
  id,
  panel,
  index,
  onChooseVisual,
}: {
  id?: string;
  panel: ProductionPanel;
  index: number;
  onChooseVisual?: (index: number) => void;
}) {
  const ratio = slideRatio(panel);
  const copy = panel.copyOnPanel?.trim();
  return (
    <BriefRowFrame
      id={id}
      stacked
      aspect={ratio}
      number={
        <>
          <span className="font-section text-[30px] font-bold leading-none text-co-text">{index + 1}</span>
          <span className="text-[15px] text-co-text">Picture</span>
          <span className="text-[12.5px] text-co-muted">{ratio}</span>
        </>
      }
      picture={<SlidePicture panel={panel} onChoose={onChooseVisual ? () => onChooseVisual(index) : undefined} />}
      body={
        <>
          <h4 className="font-sans text-[18px] font-semibold leading-snug tracking-normal text-co-text">{panel.role?.trim() || `Picture ${index + 1}`}</h4>
          {panel.composition?.trim() ? (
            <p className="mt-2.5 whitespace-pre-wrap text-[15px] leading-[1.5] text-co-text2">{panel.composition.trim()}</p>
          ) : null}
          {panel.subject?.trim() ? (
            <p className="mt-2.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 text-[13.5px] leading-snug">
              <span className="text-co-muted">Subject</span>
              <span className="text-co-text2">{panel.subject.trim()}</span>
            </p>
          ) : null}
          <RowNotes retouching={panel.retouching} referenceLabel={panel.referenceLabel} referenceUrl={panel.referenceUrl} />
        </>
      }
      lanes={
        <>
          <Lane label="Copy on the slide" value={copy} />
          {panel.imageryNote?.trim() || panel.assets?.trim() ? (
            <div>
              {panel.imageryNote?.trim() ? (
                <>
                  <FieldLabel>How it is made</FieldLabel>
                  <p className="mt-1 whitespace-pre-wrap text-[15px] leading-[1.45] text-co-text">{panel.imageryNote.trim()}</p>
                </>
              ) : null}
              {panel.assets?.trim() ? (
                <p className="mt-1.5 text-[13.5px] leading-snug text-co-muted">Assets · {panel.assets.trim()}</p>
              ) : null}
            </div>
          ) : null}
        </>
      }
    />
  );
}
